import { formatDateAsDayOfWeekMonthDayYearString } from "./date-time";

/**
 * The one placeholder for a value that is missing or unknown (visual-7). '---', '--', '-', '???' and '—' all meant
 * the same thing on different screens. The same character as MISSING_TIME_LABEL.
 */
export const EMPTY: string = '—';

/*
 * Module-scope formatters (render-11). `toLocaleString( 'en', {…} )` and `new Intl.NumberFormat` build a formatter on
 * every call, which is every table cell on every render and every synced sector alert.
 */
const PERCENT_FORMAT = new Intl.NumberFormat( 'en', { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 } );
const CURRENCY_FORMAT = new Intl.NumberFormat( 'en', { style: 'currency', currency: 'USD' } );
const WHOLE_NUMBER_FORMAT = new Intl.NumberFormat( 'en' );
const changeFormats = new Map< string, Intl.NumberFormat >();

/** A signed ('exceptZero': +1.20%, -0.40%, 0.00%) or unsigned formatter for a change, cached per shape. */
function changeFormat( style: 'percent' | 'decimal', digits: number, signed: boolean ): Intl.NumberFormat {
    const key = `${ style }|${ digits }|${ signed }`;
    let format = changeFormats.get( key );
    if ( !format ) {
        format = new Intl.NumberFormat( 'en', {
            style,
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
            signDisplay: signed ? 'exceptZero' : 'auto'
        } );
        changeFormats.set( key, format );
    }

    return format;
}

/**
 * The finite number in a value, or null. A numeric string counts: a field typed as a number can arrive from JSON as
 * "28.28", and formatLocalePercentage has always formatted one.
 */
function toFiniteNumber( value: number | string | null | undefined ): number | null {
    const number = typeof value === 'string' && value.trim() !== '' ? Number( value ) : value;
    return typeof number === 'number' && Number.isFinite( number ) ? number : null;
}

/**
 * Formats a number as a localized percentage string.
 * @param {number|null|undefined} value - The number to format as a percentage.
 * @returns {string} The formatted percentage string, or EMPTY if the value is missing or not a number.
 */
export function formatLocalePercentage( value: number | null | undefined ): string {
    const number = toFiniteNumber( value );
    return number === null ? EMPTY : PERCENT_FORMAT.format( number / 100 );
}

/**
 * Formats a pre-divided number as a localized percentage string.
 * @param {number|null|undefined} value - The number to format as a percentage.
 * @returns {string} The formatted percentage string, or EMPTY if the value is missing or not a number.
 */
export function formatLocaleDividedPercentage( value: number | null | undefined ): string {
    const number = toFiniteNumber( value );
    return number === null ? EMPTY : PERCENT_FORMAT.format( number );
}

function sanitizeInputToNumber( raw: string | null | undefined ): number {
    const cleaned = String( raw ).replace( /[^0-9.]/g, '' ).trim();
    return parseFloat( cleaned );
}

export function sanitizeInputToFloatingPointNumber( raw: string | null | undefined ): number {
    const floatingPointNumber = sanitizeInputToNumber( raw );
    if ( isNaN( floatingPointNumber ) ) {
        return 0.0;
    }

    return floatingPointNumber;
}

/**
 * Takes any string, removes any non-digits (and '.') characters, attempts to parse it into a floating point number
 * and then formats it as a fixed number of decimal places.
 */
export function sanitizeInputToStringOfValidFloatingPointNumber( raw: string | null | undefined ): string {
    const floatingPointNumber = sanitizeInputToNumber( raw );
    if ( isNaN( floatingPointNumber ) ) {
        return '';
    }

    return formatAsDecimalNumber( floatingPointNumber );
}

/**
 * Takes a number or null/undefined value and returns a valid integer number (default = 0).
 *
 * @param {number | null | undefined} val - the value to format
 * @returns {string} - the truncated value (whole number) formatted string for the passed in value.
 */
export function formatAsWholeNumber( val: number | null | undefined ): string {
    if ( !val ) {
        return '0';
    }

    return WHOLE_NUMBER_FORMAT.format( Math.floor( val ) );
}

/**
 * Takes a number and returns a valid decimal number or empty string ('').
 *
 * @param {number | null | undefined} val - the value to format
 * @param {number} numberDecimalPlaces - the number of decimal values to display
 * @returns {string} - the number value formatted as a string for the passed in parameters or empty string ('').
 */
export function formatAsDecimalNumber( val: number | null | undefined, numberDecimalPlaces: number = 2 ): string {
    if ( !val ) {
        return '';
    }

    return val.toFixed( numberDecimalPlaces );
}

/**
 * Takes a number of null/undefined value and returns a valid currency string.
 *
 * @param {number | null | undefined} val - the price value to format
 * @param {string} invalidResponseToken - the string token that is returned when the value is null or undefined
 * @returns {string} - the currency formatted string for the passed in value, or EMPTY when it is missing.
 */
export function formatPriceAsCurrency( val: number | null | undefined, invalidResponseToken?: string | null ): string {
    if ( null == val ) {
        return invalidResponseToken ?? EMPTY;
    }

    return CURRENCY_FORMAT.format( val || 0 );
}

/**
 * The direction a financial value reads as, carried from the raw value to the cell (visual-2, visual-6) rather than
 * re-derived from display text. 'flat' is a real zero; 'unknown' is a missing value.
 */
export type Tone = 'bull' | 'bear' | 'flat' | 'unknown';

/**
 * The text colour of each tone, as literal class names so Tailwind (which scans packages/) emits every one. Up is the
 * theme's bullish blue everywhere; it used to be green (text-success, 2.60:1 on white) in some columns. Flat is the
 * body text, not amber (#ffb300, 1.79:1), and unknown is the quiet text tier, never text-black.
 */
export const TONE_CLASS: Readonly< Record< Tone, string > > = {
    bull: 'text-bullish',
    bear: 'text-bearish',
    flat: 'text-base-content',
    unknown: 'text-base-content/60'
};

/** The colour names behind TONE_CLASS, for generateStatusColor's `text-${ … }` callers. */
const TONE_COLOR: Readonly< Record< Tone, string > > = {
    bull: 'bullish',
    bear: 'bearish',
    flat: 'base-content',
    unknown: 'base-content/60'
};

/**
 * How a change is expressed: 'fraction' is a ratio (0.05 is 5%), 'percent' is already in percentage points (5 is 5%),
 * 'number' is a plain amount such as index points.
 */
export type ChangeUnit = 'fraction' | 'percent' | 'number';

/** The number the formatter actually shows, so a 0.001% change that prints as 0.00% reads flat, not up. */
function roundTo( value: number, digits: number ): number {
    const scale = 10 ** digits;
    return Math.sign( value ) * Math.round( Math.abs( value ) * scale ) / scale;
}

/**
 * The tone of a number: above zero is bull, below is bear and zero is flat. A missing or non-finite value is unknown.
 * @param {number} fractionDigits - when given, the value is compared as it is shown at this many decimals.
 */
export function toneOfNumber( value: number | null | undefined, fractionDigits?: number ): Tone {
    const number = toFiniteNumber( value );
    if ( number === null ) {
        return 'unknown';
    }

    const shown = fractionDigits === undefined ? number : roundTo( number, fractionDigits );
    if ( shown > 0 ) {
        return 'bull';
    }

    return shown < 0 ? 'bear' : 'flat';
}

/** The tone of a change as formatChange() shows it. */
export function toneOfChange( value: number | null | undefined, unit: ChangeUnit = 'fraction', digits: number = 2 ): Tone {
    const shownDigits = unit === 'fraction' ? digits + 2 : digits;
    return toneOfNumber( value, shownDigits );
}

/**
 * A change with its sign: '+1.20%', '-0.40%', '0.00%' ('exceptZero'), or EMPTY when the value is missing. The sign is
 * there for members who cannot tell the bullish and bearish colours apart. `signed: false` gives the magnitude alone.
 */
export function formatChange( value: number | null | undefined, unit: ChangeUnit = 'fraction', digits: number = 2, signed: boolean = true ): string {
    const number = toFiniteNumber( value );
    if ( number === null ) {
        return EMPTY;
    }

    switch ( unit ) {
        case 'fraction':
            return changeFormat( 'percent', digits, signed ).format( number );
        case 'percent':
            return changeFormat( 'percent', digits, signed ).format( number / 100 );
        default:
            return changeFormat( 'decimal', digits, signed ).format( number );
    }
}

/**
 * The tone of a raw P&F value: an X column or a Buy signal is bull, an O column or a Sell signal is bear. Anything
 * else, including the ingest's '---' (WMD-1251), is unknown.
 */
export function toneOfSignal( value: string | null | undefined ): Tone {
    const signal = value?.trim().toLowerCase();
    if ( signal === 'x' || signal === 'buy' ) {
        return 'bull';
    }

    return signal === 'o' || signal === 'sell' ? 'bear' : 'unknown';
}

// A whole string that is a number, optionally signed and followed by '%'. parseFloat alone read '2 of 5' as 2.
const NUMERIC_LABEL = /^[+-]?(\d+(\.\d*)?|\.\d+)\s*%?$/;

/**
 * The tone of a status label or a formatted number. Prefer toneOfSignal / toneOfNumber on the raw value: a label is
 * CMS text and only the default wording is recognised here.
 */
export function toneOfLabel( value: string | null | undefined ): Tone {
    const label = value?.trim().toLowerCase();
    if ( !label ) {
        return 'unknown';
    }

    // Whole words only: includes( 'no' ) used to match 'unknown' and 'none' and paint them bearish.
    if ( label === 'strong' || label === 'buy' || label === 'x' || label.includes( 'bull' ) || /\byes\b/.test( label ) || label.includes( 'buy now' ) ) {
        return 'bull';
    }

    if ( label === 'weak' || label === 'sell' || label === 'o' || label.includes( 'bear' ) || /\bno\b/.test( label ) || label.includes( 'wait' ) ) {
        return 'bear';
    }

    return NUMERIC_LABEL.test( label ) ? toneOfNumber( parseFloat( label ) ) : 'unknown';
}

/**
 * Color codes the stock/sector status text to either red or blue
 * @param { string | null | undefined } value - The text describing the sentiment of the stock/sector.
 * @param { string | null | undefined } bullishColor - (optional) A color to use as an override for the resulting bullish color of the stock/sector. Defaults to 'bullish'
 * @param { string | null | undefined } bearishColor - (optional) A color to use as an override for the resulting bearish color of the stock/sector. Defaults to 'bearish'
 * @param { string | null | undefined } indeterminateColor - (optional) A color to use as an override for the resulting unknown color of the stock/sector. Defaults to 'base-content/60'
 * @returns { string } color the text will appear as. A numeric zero is 'base-content' (flat), not bearish.
 */
export function generateStatusColor( value: string | null | undefined, bullishColor: string = 'bullish', bearishColor: string = 'bearish', indeterminateColor: string | null = null ): string {
    const tone = toneOfLabel( value );
    switch ( tone ) {
        case 'bull':
            return bullishColor;
        case 'bear':
            return bearishColor;
        case 'flat':
            return TONE_COLOR.flat;
        default:
            // visual-6: this was 'black', which built text-black: invisible on the dark theme's card
            return indeterminateColor ?? TONE_COLOR.unknown;
    }
}

// Bullish Statuses
export const BullAlert = 'Bull Alert';
export const BullConfirmed = 'Bull Confirmed';
export const BearCorrection = 'Bear Correction';

// Bearish Statuses
export const BullCorrection = 'Bull Correction';
export const BearAlert = 'Bear Alert';
export const BearConfirmed = 'Bear Confirmed';
export const BullTop = 'Bull Top';

export function statusChangeIsBullish( value: string ) {
    return value === BullAlert || value === BullConfirmed || value === BearCorrection;
}

export function generateBpiStatusColorClass( value: string | undefined | null, bullishColor: string = 'bullish', bearishColor: string = 'bearish', indeterminateColor: string | null = null ) {
    let statusColorClass = 'base-content';
    if ( value ) {
        statusColorClass = statusChangeIsBullish( value ) ? bullishColor : bearishColor;
    }

    return statusColorClass;
}

export function formatObjectAsString( value: string | any ): string {
    let msg: string;
    if ( typeof value === "string" ) {
        msg = value;
    } else {
        msg = JSON.stringify( getCircularReplacer( value ) );
    }

    return msg;
}

export function getCircularReplacer( obj: any ) {
    "use strict";
    const objects = new WeakMap();     // object to path mappings

    return ( function recurse( value, path ) {
        // The recurse through the object, producing the deep copy.
        let old_path;   // The path of an earlier occurrence of value
        let nu: any;         // The new object or array

        // typeof null === "object", so go on if this value is really an object but not
        // one of the weird builtin objects.
        if ( typeof value === "object" && value !== null && !( value instanceof Boolean ) && !( value instanceof Date )
                && !( value instanceof Number ) && !( value instanceof RegExp ) && !( value instanceof String ) ) {

            // If the value is an object or array, look to see if we have already
            // encountered it. If so, return a {"$ref":PATH} object. This uses an
            // ES6 WeakMap.
            old_path = objects.get( value );
            if ( old_path !== undefined ) {
                return { $ref: old_path };
            }

            // Otherwise, accumulate the unique value and its path.
            objects.set( value, path );

            // If it is an array, replicate the array.
            if ( Array.isArray( value ) ) {
                nu = [];
                value.forEach( function ( element, i ) {
                    nu[ i ] = recurse( element, path + "[" + i + "]" );
                } );
            } else {
                // If it is an object, replicate the object.
                nu = {};
                Object.keys( value ).forEach( function ( name ) {
                    nu[ name ] = recurse(
                            value[ name ],
                            path + "[" + JSON.stringify( name ) + "]"
                    );
                } );
            }
            return nu;
        }
        return value;
    }( obj, "$" ) );
}

export function formatAsStandardDate( dateString: string | undefined ): string {
    if ( dateString ) {
        return formatDateAsDayOfWeekMonthDayYearString( dateString );
    }

    return EMPTY;
}

export function formatDuration( totalSeconds: number ): string {
    if ( totalSeconds < 60 ) return `${ totalSeconds } sec`;
    const hours = Math.floor( totalSeconds / 3600 );
    const minutes = Math.floor( ( totalSeconds % 3600 ) / 60 );
    if ( hours > 0 ) {
        return minutes > 0 ? `${ hours } hr ${ minutes } min watch` : `${ hours } hr watch`;
    }
    return `${ minutes } min watch`;
}

